// One composed card for a Hidden Gem: preview header, the story, a single
// primary action, an icon fact strip, and tag chips that feed back into search.

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowUpRight,
  Check,
  CalendarDays,
  Link2,
  Scale,
  User,
  type LucideIcon,
} from "@/components/icons";
import { gemSource, faviconFor } from "@/lib/gem-source";
import { creatorSlug } from "@/lib/library-creators";
import { BadgeCopyButton } from "@/components/library-badge-copy";

export type GemDetail = {
  slug: string;
  name: string;
  description: string | null;
  category: string;
  subcategory: string;
  url: string;
  domain: string;
  author: string | null;
  source_type: string | null;
  license: string | null;
  tags: string[];
  why_gem: string | null;
  og_image_url: string | null;
  og_full_url: string | null;
  created_at?: string | null;
};

function Favicon({ domain, Fallback }: { domain: string; Fallback: LucideIcon }) {
  const [failed, setFailed] = useState(false);
  if (failed || !domain) return <Fallback className="size-4 shrink-0" aria-hidden />;
  return (
    <img
      src={faviconFor(domain)}
      alt=""
      width={16}
      height={16}
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-4 shrink-0 rounded-[3px]"
    />
  );
}

function Fact({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="min-w-0">
        <dt className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </dt>
        <dd className="truncate text-sm font-medium text-foreground">{children}</dd>
      </div>
    </div>
  );
}

export function GemDetailCard({ item }: { item: GemDetail }) {
  const heroImg = item.og_full_url || item.og_image_url || "";
  const hasImg = heroImg.length > 0;
  const source = gemSource(item.source_type);
  const SourceIcon = source.icon;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://vibing.inc/library/${item.slug}`);
      setCopied(true);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  return (
    <article className="rounded-[28px] bg-card p-2 shadow-[0_1px_2px_rgba(20,20,40,0.05),0_16px_40px_-24px_rgba(20,20,40,0.28),0_40px_90px_-60px_rgba(20,20,40,0.35)]">
      {/* Preview */}
      <div className="relative overflow-hidden rounded-[20px] bg-muted">
        {hasImg ? (
          <img
            src={heroImg}
            alt={`Preview of ${item.name}`}
            fetchPriority="high"
            decoding="async"
            className="block aspect-[2/1] w-full object-cover"
          />
        ) : (
          <div className="flex aspect-[2/1] w-full items-center justify-center bg-gradient-to-br from-primary/15 to-muted">
            <span className="font-display text-6xl text-muted-foreground">
              {item.name.slice(0, 2).toUpperCase()}
            </span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 rounded-[20px] ring-1 ring-inset ring-black/10 dark:ring-white/10" />
        <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground shadow-[0_1px_2px_rgba(20,20,40,0.08),0_8px_20px_-12px_rgba(20,20,40,0.3)] backdrop-blur-md">
          <SourceIcon className="size-3.5" aria-hidden />
          {source.short}
        </span>
      </div>

      {/* Story */}
      <div className="px-5 pb-2 pt-8 md:px-10 md:pt-10">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {item.category} · {item.subcategory}
        </p>
        <h1
          className="mt-3 font-display leading-[1.03] tracking-[-0.03em] text-balance"
          style={{ fontSize: "clamp(2rem, 4.4vw, 3.25rem)" }}
        >
          {item.name}
        </h1>
        {item.description ? (
          <p className="mt-5 max-w-[62ch] text-lg leading-relaxed text-muted-foreground text-pretty">
            {item.description}
          </p>
        ) : null}

        {item.why_gem ? (
          <blockquote className="mt-8 max-w-[62ch] rounded-2xl bg-muted/60 px-6 py-5">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Why it&rsquo;s a gem
            </p>
            <p className="mt-2 text-base italic leading-relaxed text-foreground/85 text-pretty">
              {item.why_gem}
            </p>
          </blockquote>
        ) : null}

        {/* Actions */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-foreground px-6 text-sm font-medium text-background transition-[scale,opacity] duration-200 ease-[cubic-bezier(0.2,0,0,1)] hover:opacity-90 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Visit {item.domain}
            <ArrowUpRight className="size-4" aria-hidden />
          </a>
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-muted px-5 text-sm font-medium text-foreground transition-[scale,background-color] duration-200 ease-[cubic-bezier(0.2,0,0,1)] hover:bg-muted/70 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="relative flex size-4 items-center justify-center">
              <AnimatePresence initial={false} mode="wait">
                {copied ? (
                  <motion.span
                    key="check"
                    initial={{ scale: 0.25, opacity: 0, filter: "blur(4px)" }}
                    animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
                    exit={{ scale: 0.25, opacity: 0, filter: "blur(4px)" }}
                    transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Check className="size-4" aria-hidden />
                  </motion.span>
                ) : (
                  <motion.span
                    key="link"
                    initial={{ scale: 0.25, opacity: 0, filter: "blur(4px)" }}
                    animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
                    exit={{ scale: 0.25, opacity: 0, filter: "blur(4px)" }}
                    transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Link2 className="size-4" aria-hidden />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            {copied ? "Copied" : "Copy link"}
          </button>
          <BadgeCopyButton
            slug={item.slug}
            placement="down"
            triggerSize="md"
            className="min-h-[44px] items-center"
          />
        </div>
      </div>

      {/* Fact strip */}
      <dl className="mt-8 grid grid-cols-1 gap-5 border-t border-border/60 px-5 py-7 sm:grid-cols-2 lg:grid-cols-4 md:px-10">
        {item.author ? (
          <Fact icon={User} label="Author">
            <Link
              to="/library/by/$creator"
              params={{ creator: creatorSlug(item.author) }}
              className="underline-offset-4 hover:underline"
            >
              {item.author}
            </Link>
          </Fact>
        ) : null}
        <Fact icon={SourceIcon} label="Type">
          {source.label}
        </Fact>
        {item.license ? (
          <Fact icon={Scale} label="License">
            {item.license}
          </Fact>
        ) : null}
        {item.created_at ? (
          <Fact icon={CalendarDays} label="Added">
            <span className="tabular-nums">
              {new Date(item.created_at).toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
          </Fact>
        ) : null}
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Favicon domain={item.domain} Fallback={SourceIcon} />
          </span>
          <div className="min-w-0">
            <dt className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Domain
            </dt>
            <dd className="truncate text-sm font-medium">
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-4 hover:underline"
              >
                {item.domain}
              </a>
            </dd>
          </div>
        </div>
      </dl>

      {/* Tags */}
      {item.tags.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-t border-border/60 px-5 py-6 md:px-10">
          {item.tags.map((t) => (
            <Link
              key={t}
              to="/library"
              search={{ q: t }}
              className="inline-flex min-h-[40px] items-center rounded-full bg-muted px-4 text-xs text-muted-foreground transition-[scale,color,background-color] duration-200 ease-[cubic-bezier(0.2,0,0,1)] hover:bg-muted/70 hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t}
            </Link>
          ))}
        </div>
      ) : null}
    </article>
  );
}
